(() => {
  if (window.FloraBestsellerSort) return;
  window.FloraBestsellerSort = true;

  const selector = '[data-flora-bestsellers]';
  const cacheLifetime = 60 * 60 * 1000;
  let pendingRanking;

  const endpoint = (grid, page) => {
    const root = grid.dataset.floraRoot || '/';
    const url = new URL(root.replace(/\/?$/, '/') + 'collections/all', window.location.origin);
    url.searchParams.set('sort_by', 'best-selling');
    url.searchParams.set('section_id', 'flora-bestseller-ranking');
    url.searchParams.set('page', String(page));
    const preview = new URLSearchParams(window.location.search).get('preview_theme_id');
    if (preview) url.searchParams.set('preview_theme_id', preview);
    return url;
  };

  const fetchPage = async (grid, page) => {
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), 10000);
    try {
      const response = await fetch(endpoint(grid, page), {
        credentials: 'same-origin',
        signal: controller.signal
      });
      if (!response.ok) throw new Error('Ranking request failed');
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const element = html.getElementById('flora-bestseller-ranking-data');
      if (!element) throw new Error('Ranking data unavailable');
      const data = JSON.parse(element.textContent);
      if (data.version !== 1 || data.sort !== 'best-selling' || data.collection !== 'all'
        || data.page !== page || !Number.isInteger(data.pages) || data.pages < 1
        || data.pages > 100 || !Number.isInteger(data.total) || data.total < 1
        || !Array.isArray(data.handles) || !data.handles.length
        || data.handles.some((handle) => typeof handle !== 'string' || !handle)) {
        throw new Error('Invalid ranking data');
      }
      return data;
    } finally {
      window.clearTimeout(timer);
    }
  };

  const loadRanking = async (grid) => {
    const cacheKey = 'flora-bestsellers-v1:' + grid.dataset.floraTheme + ':' + grid.dataset.floraRoot;
    try {
      const saved = JSON.parse(window.localStorage.getItem(cacheKey));
      if (saved && Number.isFinite(saved.savedAt) && Date.now() >= saved.savedAt
        && Date.now() - saved.savedAt < cacheLifetime
        && Array.isArray(saved.handles) && saved.handles.length
        && saved.handles.every((handle) => typeof handle === 'string' && handle)
        && new Set(saved.handles).size === saved.handles.length) {
        return saved;
      }
    } catch {}

    const first = await fetchPage(grid, 1);
    const pages = [first];
    for (let page = 2; page <= first.pages; page += 3) {
      const batch = await Promise.all(
        Array.from({ length: Math.min(3, first.pages - page + 1) },
          (_, offset) => fetchPage(grid, page + offset))
      );
      pages.push(...batch);
    }
    if (pages.some((page) => page.pages !== first.pages || page.total !== first.total)) {
      throw new Error('Ranking changed during pagination');
    }
    const handles = pages.flatMap((page) => page.handles);
    if (handles.length !== first.total || new Set(handles).size !== handles.length) {
      throw new Error('Incomplete ranking data');
    }
    const saved = { savedAt: Date.now(), handles };
    try {
      window.localStorage.setItem(cacheKey, JSON.stringify(saved));
    } catch {}
    return saved;
  };

  const getRanking = (grid) => {
    if (!pendingRanking) {
      pendingRanking = loadRanking(grid).catch((error) => {
        pendingRanking = undefined;
        throw error;
      });
    }
    return pendingRanking;
  };

  const applyRanking = (grid, ranking) => {
    if (!grid.isConnected) return;
    const items = Array.from(grid.children);
    if (items.length < 2 || items.some((item) => !item.dataset.floraProduct)) return;
    const rank = new Map(ranking.handles.map((handle, index) => [handle, index]));
    const ordered = items.map((item, index) => ({
      item,
      index,
      rank: rank.get(item.dataset.floraProduct) ?? Number.MAX_SAFE_INTEGER
    })).sort((a, b) => a.rank - b.rank || a.index - b.index).map(({ item }) => item);
    const carousel = grid.closest('carousel-slider');
    const slider = carousel?.querySelector('.slider');
    if (grid.contains(document.activeElement) || (slider && Math.abs(slider.scrollLeft) > 1)) {
      grid.dataset.floraSortStatus = 'deferred';
      return;
    }
    const changed = ordered.some((item, index) => item !== items[index]);
    if (changed) {
      ordered.forEach((item, index) => {
        grid.appendChild(item);
        if (item.hasAttribute('data-cc-animate-delay')) {
          item.setAttribute('data-cc-animate-delay', (index + 1) * 0.07 + 's');
        }
      });
      if (carousel) {
        carousel.slides = ordered.filter((item) => !item.hasAttribute('hidden'));
        if (carousel.slider && typeof carousel.handleBreakpointChange === 'function') {
          carousel.currentIndex = 0;
          carousel.handleBreakpointChange();
          grid.dataset.floraCarouselRefreshed = 'true';
        }
      }
    }
    grid.dataset.floraSortStatus = 'ready';
    grid.dataset.floraSortUpdated = String(ranking.savedAt);
    grid.dataset.floraSortUnranked = String(items.filter((item) => !rank.has(item.dataset.floraProduct)).length);
    grid.dispatchEvent(new CustomEvent('flora:bestsellers-sorted', { bubbles: true }));
  };

  const sortWithin = async (root) => {
    const grids = Array.from(root.querySelectorAll(selector));
    if (root.matches?.(selector)) grids.unshift(root);
    const eligible = grids.filter((grid) => grid.querySelectorAll('[data-flora-product]').length > 1);
    if (!eligible.length) return;
    eligible.forEach((grid) => { grid.dataset.floraSortStatus = 'loading'; });
    try {
      const ranking = await getRanking(eligible[0]);
      eligible.forEach((grid) => applyRanking(grid, ranking));
    } catch {
      eligible.forEach((grid) => { grid.dataset.floraSortStatus = 'unavailable'; });
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => sortWithin(document), { once: true });
  } else {
    sortWithin(document);
  }
  document.addEventListener('shopify:section:load', (event) => sortWithin(event.target));
})();